// ============================================================
// Projects.js — Animated project cards with viewport reveal
// ============================================================
import React, { useEffect, useRef } from "react";
import { projects } from "../../portfolioData";
import "./Projects.css";

// Individual project card
const ProjectCard = ({ project, index }) => {
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible");
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="project-card"
      ref={cardRef}
      style={{ "--delay": `${index * 0.15}s`, "--accent": project.color }}
    >
      {/* Project banner — shows image if provided, else colored gradient */}
      <div
        className="card-banner"
        style={
          project.image
            ? {}
            : { background: `linear-gradient(135deg, ${project.color}20, ${project.color}40)` }
        }
      >
        {project.image ? (
          <img src={project.image} alt={project.title} className="card-banner-img" />
        ) : (
          <>
            <div className="card-icon" style={{ background: project.color }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5">
                <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3m13 0h3a2 2 0 002-2v-3" />
              </svg>
            </div>
            <div className="banner-dots" />
          </>
        )}
      </div>

      {/* Card content */}
      <div className="card-body">
        <div className="card-title-row">
          <h3 className="card-title">{project.title}</h3>

          <span
            className={`project-status ${
              project.status === "deployed"
                ? "status-deployed"
                : project.status === "building"
                ? "status-building"
                : "status-dev"
            }`}
          >
            {project.status === "deployed"
              ? "✦ Deployed"
              : project.status === "building"
              ? "🚧 Under Build"
              : "⚙ In Development"}
          </span>
        </div>
        <p className="card-desc">{project.description}</p>
        <div className="card-tech">
          {project.tech.map((t) => (
            <span key={t} className="tech-badge">
              {t}
            </span>
          ))}
        </div>

        {/* Action buttons — only shown when enabled in portfolioData.js */}
        {(project.showGithub || project.showLive || project.showWebsite) && (
          <div className="card-actions">
            {project.showGithub && (
              <a
                href={project.github}
                target="_blank"
                rel="noreferrer"
                className="card-btn btn-github"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.477 2 2 6.477 2 12c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.009-.866-.014-1.7-2.782.604-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.464-1.11-1.464-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.115 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
                </svg>
                GitHub
              </a>
            )}
            {project.showLive && (
              <a
                href={project.live}
                target="_blank"
                rel="noreferrer"
                className="card-btn btn-live"
                style={{ background: project.color }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                Live Demo
              </a>
            )}
            {project.showWebsite && (
              <a
                href={project.website}
                target="_blank"
                rel="noreferrer"
                className="card-btn btn-live"
                style={{ background: project.color }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                Website
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Projects = () => {
  return (
    <section id="projects" className="section projects-section">
      <div className="section-container">
        {/* Section header */}
        <div className="section-header">
          <span className="section-tag">Portfolio</span>
          <h2 className="section-title">Featured Projects</h2>
          <p className="section-desc">
            Real-world applications built with modern tech stacks — from EV planning to real-time streaming.
          </p>
        </div>

        {/* Cards grid */}
        <div className="projects-grid">
          {projects.map((project, i) => (
            <ProjectCard key={project.id} project={project} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
